// utils/sendAndPublish.js
//
// Send for Approval and Publish, from the editor and from the main page (Save as Draft design v4.0, sections 4,
// 5, 12 and 13; wireframe jobs-library-ownership-v3.html: the Send for Approval page, the "Publish without a
// review?" box, the main-page Send / Publish buttons, the toasts). Pure helpers, so the box, the approver page,
// the button rules, the payloads and the toasts are tested without rendering the page. The server makes the
// final call on every rule (STALE, NOT_HOLDER, IN_REVIEW, UNCHANGED, the approver checks).

import { JD_STATUSES } from 'constants/jobs.constants';
import { SAVE_AS_DRAFT_LABEL } from 'Utils/saveAsDraft';
import { OUT_OF_SCOPE_MESSAGE, OUT_OF_SCOPE_REASON } from 'Utils/jobDetailsPageState';

// ── Wording (wireframe, unless stated) ───────────────────────────────────────────
export const SEND_FOR_APPROVAL_LABEL = 'Send for Approval';
export const PUBLISH_LABEL = 'Publish';
// Main-page tooltips (wireframe toolbar)
export const TOOLTIP_SEND_MAIN = 'Submit this JD to an HR Manager for review. All drafts become visible to everyone.';
export const TOOLTIP_PUBLISH_MAIN = 'Publish this JD immediately without requiring approval.';
// Main page, latest version UNPUBLISHED and unchanged since it was published (design, Button rules)
export const TOOLTIP_SEND_UNCHANGED = 'Make changes before sending for approval';
export const TOOLTIP_PUBLISH_UNCHANGED = 'Make changes before publishing';

// The shared Send for Approval (Assign Approver) page: the wireframe's "send" modal
export const ASSIGN_APPROVER = {
  title: 'Send for Approval',
  // the wireframe's body, with the JD title in bold between the two parts
  bodyBefore: 'Select an HR Manager to review ',
  bodyAfter: '. Sending makes this JD and all its drafts visible to everyone.',
  approverLabel: 'Approver',
  placeholder: 'Search HR Managers by name…',
  cancelLabel: 'Cancel',
  sendLabel: SEND_FOR_APPROVAL_LABEL,
  // Not in the wireframe; the designs say "disabled with a message if nobody is available" and, for view scope,
  // "the picker says no one has access" (Ownership design, part 8 and section 13)
  noApproverMessage: 'No HR Manager with the approver right has access to this JD, so it can\'t be sent for '
    + 'approval yet.',
  loadFailedMessage: "The list of HR Managers couldn't be loaded. Close this and try again.",
};

// "Publish without a review?" (HR Managers, every time, no "Don't ask again"; Close / Esc does nothing)
export const PUBLISH_BOX = {
  title: 'Publish without a review?',
  body: "This version goes live across SkillsBridge straight away. No other HR Manager will check it, and it can't be edited until it's unpublished. Publishing also hides versions from before the last published one from your version history and Compare. Would you like someone to review it first, or keep working on it?",
  // the wireframe's second, secondary line under the body
  secondary: 'All drafts become visible to everyone, and older New and Draft versions become Historical.',
  sendLabel: SEND_FOR_APPROVAL_LABEL,
  saveDraftLabel: SAVE_AS_DRAFT_LABEL,
  publishLabel: 'Yes, publish now',
  cancelLabel: 'Cancel',
};

// The buttons of the box: Cancel (closes the box, nothing happens), then the wireframe's Send for Approval (to the
// approver page), Save as Draft (only when opened from the editor), Yes, publish now (primary).
export const PUBLISH_BOX_ACTION = { CANCEL: 'cancel', SEND: 'send', SAVE_DRAFT: 'saveDraft', PUBLISH: 'publish' };
export const getPublishBoxButtons = ({ fromEditor }) => [
  { action: PUBLISH_BOX_ACTION.CANCEL, label: PUBLISH_BOX.cancelLabel, primary: false },
  { action: PUBLISH_BOX_ACTION.SEND, label: PUBLISH_BOX.sendLabel, primary: false },
  ...(fromEditor ? [{ action: PUBLISH_BOX_ACTION.SAVE_DRAFT, label: PUBLISH_BOX.saveDraftLabel, primary: false }] : []),
  { action: PUBLISH_BOX_ACTION.PUBLISH, label: PUBLISH_BOX.publishLabel, primary: true },
];

// Toasts: the design's "…Your draft is now visible…" only when a draft the user had saved earlier (one that existed
// before this action) became visible; the wireframe's plain ones otherwise (e.g. a NEW v1.00 edited in the editor
// and sent: the draft made in that same request was never seen as a draft).
export const SENT_DRAFT_TOAST = 'Sent for approval. Your draft is now visible to others.';
export const PUBLISHED_DRAFT_TOAST = 'Published. Your draft is now visible to everyone.';
export const SENT_TOAST = 'Sent for approval.';
export const PUBLISHED_TOAST = 'JD published.';
export const getSentToast = (hadDraft) => (hadDraft ? SENT_DRAFT_TOAST : SENT_TOAST);
export const getPublishedToast = (hadDraft) => (hadDraft ? PUBLISHED_DRAFT_TOAST : PUBLISHED_TOAST);
// Did the JD have a saved draft before this action? Drafts are always the newest versions, so the latest version
// the page loaded is a DRAFT exactly when one existed.
export const hadSavedDraft = (latestStatusBefore) => latestStatusBefore === JD_STATUSES.DRAFT;

// ── Main-page button rule ────────────────────────────────────────────────────────
// Send and Publish are disabled only when the latest version is UNPUBLISHED and unchanged since it was
// published (any change is a draft on top, so the latest is then a DRAFT). A NEW v1.00, Revise results and
// drafts can be sent or published as they are.
export const isUnchangedUnpublished = (latestStatus) => latestStatus === JD_STATUSES.UNPUBLISHED;

// ── The approver page ────────────────────────────────────────────────────────────
const norm = (email) => (typeof email === 'string' ? email.trim().toLowerCase() : '');

// The candidates the server lists (HR Managers with the approver right, never the caller), never the owner.
export const getApproverOptions = (candidates, ownerEmail) => (Array.isArray(candidates) ? candidates : [])
  .filter((c) => c?.value && norm(c.value) !== norm(ownerEmail))
  .map((c) => ({ value: c.value, label: `${c.label || c.value} (HR Manager)` }));

// A review an HR Admin reassigned goes back to that approver on the resubmit (Ownership design, part 5): the picker
// is pre-set to them and locked. { selected, note, unavailable } — or null when the review wasn't reassigned.
export const getReassignedNote = (name) => `This review was reassigned to ${name} by an HR Admin, so it goes back `
  + 'to them.';
export const REASSIGNED_UNAVAILABLE = "The approver an HR Admin reassigned this review to can't approve it now. Ask an "
  + 'HR Admin to reassign the review.';
export const getLockedApprover = (options, reassignedEmail) => {
  if (!norm(reassignedEmail)) return null;
  const match = (options || []).find((o) => norm(o.value) === norm(reassignedEmail));
  if (!match) return { selected: null, note: REASSIGNED_UNAVAILABLE, unavailable: true };
  return { selected: match.value, note: getReassignedNote(String(match.label || match.value).replace(/ \(.*\)$/, '')),
    unavailable: false };
};

// The approver list failed because the JD is outside the caller's view scope (403 OUT_OF_SCOPE)
export const isOutOfScopeError = (error) => error?.response?.data?.reason === OUT_OF_SCOPE_REASON;

// Send stays greyed out until someone is picked; with nobody available it is disabled with a message. outOfScope:
// the list was refused because the JD is outside the caller's view scope (said as such, not as a load failure).
export const getAssignApproverState = ({ options, selected, loading, loadFailed, outOfScope = false }) => {
  if (loading) return { canSend: false, message: null };
  if (outOfScope) return { canSend: false, message: OUT_OF_SCOPE_MESSAGE };
  if (loadFailed) return { canSend: false, message: ASSIGN_APPROVER.loadFailedMessage };
  if (!options?.length) return { canSend: false, message: ASSIGN_APPROVER.noApproverMessage };
  return { canSend: !!selected && options.some((o) => o.value === selected), message: null };
};

// ── Request bodies (the stale-check values come from getStaleCheckValues in Utils/saveAsDraft) ───────────────
// The editor's Send / Publish: POST /approval/save_revision, the edits saved and sent or published in one request.
// For a Send nothing is stored until Send is clicked on the approver page, so the approver comes with it.
export const buildEditorSendPayload = ({ jobId, content, staleCheck, approver }) => ({
  save_mode: 'send',
  job_id: jobId,
  job_desc_md: content,
  generated_job_desc_md: content,
  assigned_approver: approver,
  ...staleCheck,
});

export const buildEditorPublishPayload = ({ jobId, content, staleCheck }) => ({
  save_mode: 'publish',
  job_id: jobId,
  job_desc_md: content,
  generated_job_desc_md: content,
  ...staleCheck,
});

// The main page's Publish: POST /self_publish_jd, the latest version as it is
export const buildSelfPublishPayload = ({ jobId, staleCheck }) => ({
  sb_unique_id: staleCheck?.latest_sb_unique_id ?? null,
  job_id: jobId,
  jd_status: JD_STATUSES.PUBLISHED,
  is_jd_content_changed: false,
  ...staleCheck,
});

// ── Refusals ─────────────────────────────────────────────────────────────────────
export const SEND_FAILED_TITLE = 'Not sent for approval';
export const PUBLISH_FAILED_TITLE = 'Not published';
const FALLBACK = {
  send: 'The JD could not be sent for approval. Please try again.',
  publish: 'The JD could not be published. Please try again.',
};

/**
 * { reason, message, isStale, savedDraft } for a failed send or publish (an axios error): the server's message
 * first (it is worded for the action). savedDraft: the ids of the draft the editor's changes were saved as when
 * the send or publish itself was then refused (SAVED_NOT_SENT / SAVED_NOT_PUBLISHED).
 */
export const getSendOrPublishError = (error, action) => {
  const data = error?.response?.data || {};
  const reason = data.reason || null;
  const saved = (reason === 'SAVED_NOT_SENT' || reason === 'SAVED_NOT_PUBLISHED') && data.sb_unique_id
    ? { sb_unique_id: data.sb_unique_id, last_updated_time_stamp: data.last_updated_time_stamp ?? null }
    : null;
  return {
    reason,
    isStale: reason === 'STALE',
    savedDraft: saved,
    message: data.message || data.error || FALLBACK[action] || FALLBACK.send,
  };
};

// ── Archive (JD Archive): a JD whose family status is DRAFT has drafts (they are always the newest versions) ──
export const ARCHIVE_DRAFTS_CONFIRM = {
  title: 'Archive Job Description',
  body: 'This JD has drafts. Do you still wish to archive it?',
  cancelLabel: 'Cancel',
  continueLabel: 'Archive',
};
export const jdHasDrafts = (record) => record?.family_status === JD_STATUSES.DRAFT;
